// ============================================================================
// Entity: Skills
// Key: skills
// Kind: entity
// Generated file. Do not edit manually.
// ============================================================================

import {
	getSkillGroupsListQueryKey,
	getSkillsDetailQueryKey,
	getSkillsDetailQueryOptions,
	getSkillsListQueryKey,
	getSkillsListQueryOptions,
	getSkillsRelationsSkillGroupsAssignedQueryKey,
	getSkillsRelationsSkillGroupsAssignedQueryOptions,
	getSkillsRelationsSkillGroupsAvailableQueryKey,
	getSkillsRelationsSkillGroupsAvailableQueryOptions,
	getSkillsRelationsTeamsAssignedQueryKey,
	getSkillsRelationsTeamsAssignedQueryOptions,
	getSkillsRelationsTeamsAvailableQueryKey,
	getSkillsRelationsTeamsAvailableQueryOptions,
	getSkillsRelationsUsersAssignedQueryKey,
	getSkillsRelationsUsersAssignedQueryOptions,
	getSkillsRelationsUsersAvailableQueryKey,
	getSkillsRelationsUsersAvailableQueryOptions,
	getTeamsListQueryKey,
	getUsersListQueryKey,
	skillsCreate,
	skillsDelete,
	skillsRelationsSkillGroupsAssign,
	skillsRelationsSkillGroupsUnassign,
	skillsRelationsTeamsAssign,
	skillsRelationsTeamsUnassign,
	skillsRelationsUsersAssign,
	skillsRelationsUsersUnassign,
	skillsUpdate,
} from '../api/backstage'
// #region Imports
import type {
	CreateOrUpdateSkillRequest,
	ExtendedUserDto,
	PagedResultOfExtendedUserDto,
	PagedResultOfSkillGroupSkillDto,
	PagedResultOfSkillGroupSummary,
	PagedResultOfSkillSummary,
	PagedResultOfTeamSkillResponse,
	PagedResultOfTeamSummary,
	SkillDetails,
	SkillGroupSkillDto,
	SkillSummary,
	TeamSkillResponse,
} from '../api/backstage.schemas'
import type { EntityConfig, ListQueryParams } from './entity-config.types'
import {
	defineMutationEndpoint,
	defineQueryEndpoint,
} from './entity-config.types'

// #endregion

// #region Entity Endpoints
const skillsEntityEndpoints = {
	create: defineMutationEndpoint<
		SkillSummary,
		CreateOrUpdateSkillRequest,
		undefined
	>({
		key: 'skills.create',
		method: 'POST',
		path: '/admin/skills',
		mutationOptions: () => ({
			mutationFn: (variables: { data: CreateOrUpdateSkillRequest }) =>
				skillsCreate(variables.data),
		}),
		invalidates: [(_context) => getSkillsListQueryKey()],
	}),
	delete: defineMutationEndpoint<void, undefined, { skillId: number }>({
		key: 'skills.delete',
		method: 'DELETE',
		path: '/admin/skills/{skillId}',
		pathParams: ['skillId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				skillId: number | string | null | undefined
			}) => skillsDelete(variables.skillId),
		}),
		invalidates: [(_context) => getSkillsListQueryKey()],
	}),
	detail: defineQueryEndpoint<SkillDetails, { skillId: number }, undefined>({
		key: 'skills.detail',
		method: 'GET',
		path: '/admin/skills/{skillId}',
		pathParams: ['skillId'] as const,
		queryKey: (args) => getSkillsDetailQueryKey(args?.pathParams?.skillId),
		queryOptions: (args) =>
			getSkillsDetailQueryOptions(args?.pathParams?.skillId),
	}),
	list: defineQueryEndpoint<
		PagedResultOfSkillSummary,
		undefined,
		ListQueryParams
	>({
		key: 'skills.list',
		method: 'GET',
		path: '/admin/skills',
		queryKey: (args) => getSkillsListQueryKey(args?.queryParams),
		queryOptions: (args) => getSkillsListQueryOptions(args?.queryParams),
	}),
	update: defineMutationEndpoint<
		SkillSummary,
		CreateOrUpdateSkillRequest,
		{ skillId: number }
	>({
		key: 'skills.update',
		method: 'PUT',
		path: '/admin/skills/{skillId}',
		pathParams: ['skillId'] as const,
		mutationOptions: () => ({
			mutationFn: (variables: {
				skillId: number | string | null | undefined
				data: CreateOrUpdateSkillRequest
			}) => skillsUpdate(variables.skillId, variables.data),
		}),
		invalidates: [
			(_context) => getSkillsListQueryKey(),
			(context) => getSkillsDetailQueryKey(context.entityId ?? context.ownerId),
		],
	}),
} as const
// #endregion

// #region Relations
// #region Relation: SkillGroups
// Target Entity: skillGroups
const skillsSkillGroupsRelation = {
	key: 'skillGroups',
	targetEntity: 'skillGroups',
	endpoints: {
		assign: defineMutationEndpoint<
			SkillGroupSkillDto[],
			(number | string)[],
			{ skillId: number }
		>({
			key: 'skills.relations.skillGroups.assign',
			method: 'POST',
			path: '/admin/skills/{skillId}/skillgroups',
			pathParams: ['skillId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillsRelationsSkillGroupsAssign(variables.skillId, variables.data),
			}),
			invalidates: [
				(context) =>
					getSkillsRelationsSkillGroupsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillsRelationsSkillGroupsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillsListQueryKey(),
				(_context) => getSkillGroupsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfSkillGroupSkillDto,
			{ skillId: number },
			ListQueryParams
		>({
			key: 'skills.relations.skillGroups.assigned',
			method: 'GET',
			path: '/admin/skills/{skillId}/skillgroups',
			pathParams: ['skillId'] as const,
			queryKey: (args) =>
				getSkillsRelationsSkillGroupsAssignedQueryKey(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillsRelationsSkillGroupsAssignedQueryOptions(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfSkillGroupSummary,
			{ skillId: number },
			ListQueryParams
		>({
			key: 'skills.relations.skillGroups.available',
			method: 'GET',
			path: '/admin/skills/{skillId}/assignable-skillgroups',
			pathParams: ['skillId'] as const,
			queryKey: (args) =>
				getSkillsRelationsSkillGroupsAvailableQueryKey(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillsRelationsSkillGroupsAvailableQueryOptions(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ skillId: number }
		>({
			key: 'skills.relations.skillGroups.unassign',
			method: 'DELETE',
			path: '/admin/skills/{skillId}/skillgroups',
			pathParams: ['skillId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillId: number | string | null | undefined
					data: (number | string)[]
				}) =>
					skillsRelationsSkillGroupsUnassign(variables.skillId, variables.data),
			}),
			invalidates: [
				(context) =>
					getSkillsRelationsSkillGroupsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillsRelationsSkillGroupsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillsListQueryKey(),
				(_context) => getSkillGroupsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Teams
// Target Entity: teams
const skillsTeamsRelation = {
	key: 'teams',
	targetEntity: 'teams',
	endpoints: {
		assign: defineMutationEndpoint<
			TeamSkillResponse[],
			(number | string)[],
			{ skillId: number }
		>({
			key: 'skills.relations.teams.assign',
			method: 'POST',
			path: '/admin/skills/{skillId}/teams',
			pathParams: ['skillId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillId: number | string | null | undefined
					data: (number | string)[]
				}) => skillsRelationsTeamsAssign(variables.skillId, variables.data),
			}),
			invalidates: [
				(context) =>
					getSkillsRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillsRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillsListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfTeamSkillResponse,
			{ skillId: number },
			ListQueryParams
		>({
			key: 'skills.relations.teams.assigned',
			method: 'GET',
			path: '/admin/skills/{skillId}/teams',
			pathParams: ['skillId'] as const,
			queryKey: (args) =>
				getSkillsRelationsTeamsAssignedQueryKey(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillsRelationsTeamsAssignedQueryOptions(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfTeamSummary,
			{ skillId: number },
			ListQueryParams
		>({
			key: 'skills.relations.teams.available',
			method: 'GET',
			path: '/admin/skills/{skillId}/assignable-teams',
			pathParams: ['skillId'] as const,
			queryKey: (args) =>
				getSkillsRelationsTeamsAvailableQueryKey(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillsRelationsTeamsAvailableQueryOptions(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ skillId: number }
		>({
			key: 'skills.relations.teams.unassign',
			method: 'DELETE',
			path: '/admin/skills/{skillId}/teams',
			pathParams: ['skillId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillId: number | string | null | undefined
					data: (number | string)[]
				}) => skillsRelationsTeamsUnassign(variables.skillId, variables.data),
			}),
			invalidates: [
				(context) =>
					getSkillsRelationsTeamsAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillsRelationsTeamsAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillsListQueryKey(),
				(_context) => getTeamsListQueryKey(),
			],
		}),
	},
} as const
// #endregion

// #region Relation: Users
// Target Entity: users
const skillsUsersRelation = {
	key: 'users',
	targetEntity: 'users',
	endpoints: {
		assign: defineMutationEndpoint<
			ExtendedUserDto[],
			(number | string)[],
			{ skillId: number }
		>({
			key: 'skills.relations.users.assign',
			method: 'POST',
			path: '/admin/skills/{skillId}/users',
			pathParams: ['skillId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillId: number | string | null | undefined
					data: (number | string)[]
				}) => skillsRelationsUsersAssign(variables.skillId, variables.data),
			}),
			invalidates: [
				(context) =>
					getSkillsRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillsRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillsListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
		assigned: defineQueryEndpoint<
			PagedResultOfExtendedUserDto,
			{ skillId: number },
			ListQueryParams
		>({
			key: 'skills.relations.users.assigned',
			method: 'GET',
			path: '/admin/skills/{skillId}/users',
			pathParams: ['skillId'] as const,
			queryKey: (args) =>
				getSkillsRelationsUsersAssignedQueryKey(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillsRelationsUsersAssignedQueryOptions(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
		}),
		available: defineQueryEndpoint<
			PagedResultOfExtendedUserDto,
			{ skillId: number },
			ListQueryParams
		>({
			key: 'skills.relations.users.available',
			method: 'GET',
			path: '/admin/skills/{skillId}/assignable-users',
			pathParams: ['skillId'] as const,
			queryKey: (args) =>
				getSkillsRelationsUsersAvailableQueryKey(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
			queryOptions: (args) =>
				getSkillsRelationsUsersAvailableQueryOptions(
					args?.pathParams?.skillId,
					args?.queryParams,
				),
		}),
		unassign: defineMutationEndpoint<
			void,
			(number | string)[],
			{ skillId: number }
		>({
			key: 'skills.relations.users.unassign',
			method: 'DELETE',
			path: '/admin/skills/{skillId}/users',
			pathParams: ['skillId'] as const,
			mutationOptions: () => ({
				mutationFn: (variables: {
					skillId: number | string | null | undefined
					data: (number | string)[]
				}) => skillsRelationsUsersUnassign(variables.skillId, variables.data),
			}),
			invalidates: [
				(context) =>
					getSkillsRelationsUsersAssignedQueryKey(
						context.ownerId ?? context.entityId,
					),
				(context) =>
					getSkillsRelationsUsersAvailableQueryKey(
						context.ownerId ?? context.entityId,
					),
				(_context) => getSkillsListQueryKey(),
				(_context) => getUsersListQueryKey(),
			],
		}),
	},
} as const
// #endregion
// #endregion

// #region Entity Config Export
export const skillsEntityConfig = {
	key: 'skills',
	kind: 'entity',
	entity: {
		idField: 'id',
		idParam: 'skillId',
		routePath: '/skills',
	},
	endpoints: skillsEntityEndpoints,
	relations: {
		skillGroups: skillsSkillGroupsRelation,
		teams: skillsTeamsRelation,
		users: skillsUsersRelation,
	},
} as const satisfies EntityConfig<'skills'>
// #endregion
